import { ComponentPortal } from '@angular/cdk/portal';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { Component, ComponentRef, Directive, ElementRef, HostListener, Input, OnDestroy, ViewContainerRef } from '@angular/core';

@Component({
    selector: 'je-tooltip-panel',
    standalone: true,
    host: {
        class: 'pointer-events-none',
    },
    template: `
        <div
            class="pointer-events-none max-w-sm rounded-lg border border-black-700 bg-black-900 px-3 py-2 text-xs font-medium leading-4 text-white shadow-xl ring-1 ring-black-700/60 backdrop-blur-sm">
            {{ text }}
        </div>
    `,
})
class JeTooltipPanelComponent {
    @Input() text = '';
}

@Directive({
    selector: '[jeTooltip]',
    standalone: true,
})
export class JeTooltipDirective implements OnDestroy {
    @Input() public jeTooltip: string | null = null;
    @Input() public jeTooltipShowDelay: number | string | null = null;

    private overlayRef: OverlayRef | null = null;
    private panelRef: ComponentRef<JeTooltipPanelComponent> | null = null;
    private showTimeoutId: ReturnType<typeof setTimeout> | null = null;

    constructor(
        private overlay: Overlay,
        private elementRef: ElementRef<HTMLElement>,
        private viewContainerRef: ViewContainerRef,
    ) {}

    @HostListener('mouseenter')
    @HostListener('focusin')
    public scheduleShow(): void {
        this.clearShowTimer();

        if (!this.getTooltipText()) {
            return;
        }

        this.showTimeoutId = setTimeout(() => this.show(), this.getShowDelay());
    }

    @HostListener('mouseleave')
    @HostListener('focusout')
    public hide(): void {
        this.clearShowTimer();
        this.overlayRef?.detach();
        this.panelRef = null;
    }

    @HostListener('keydown.escape')
    public onEscape(): void {
        this.hide();
    }

    public ngOnDestroy(): void {
        this.clearShowTimer();
        this.overlayRef?.dispose();
        this.overlayRef = null;
        this.panelRef = null;
    }

    private show(): void {
        const tooltipText = this.getTooltipText();
        if (!tooltipText) {
            return;
        }

        if (!this.overlayRef) {
            const positionStrategy = this.overlay
                .position()
                .flexibleConnectedTo(this.elementRef)
                .withPush(true)
                .withPositions([
                    { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
                    { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
                    { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8 },
                    { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8 },
                ]);

            this.overlayRef = this.overlay.create({
                positionStrategy,
                scrollStrategy: this.overlay.scrollStrategies.reposition(),
                disposeOnNavigation: true,
            });
        }

        if (!this.overlayRef.hasAttached()) {
            const portal = new ComponentPortal(JeTooltipPanelComponent, this.viewContainerRef);
            this.panelRef = this.overlayRef.attach(portal);
        }

        if (this.panelRef) {
            this.panelRef.instance.text = tooltipText;
            this.panelRef.changeDetectorRef.detectChanges();
        }

        this.overlayRef.updatePosition();
    }

    private clearShowTimer(): void {
        if (this.showTimeoutId) {
            clearTimeout(this.showTimeoutId);
            this.showTimeoutId = null;
        }
    }

    private parseDelay(value: number | string | null | undefined): number {
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    }

    private getTooltipText(): string {
        return (this.jeTooltip ?? '').trim();
    }

    private getShowDelay(): number {
        return this.parseDelay(this.jeTooltipShowDelay);
    }
}
